/**
 * 匹配结果预览组件
 */

import React from 'react';
import { Card, Collapse, Tag } from 'dingtalk-design-desktop';
import type { MatchingResult, FieldInfo } from '../types';

const { Panel } = Collapse;

interface MessagePreviewProps {
  matchingResult: MatchingResult;
  fields: FieldInfo[];
}

export const MessagePreview: React.FC<MessagePreviewProps> = ({
  matchingResult,
  fields,
}) => {
  /**
   * 获取记录的显示文本
   */
  const getRecordDisplayText = (record: any): string => {
    if (!record.fields) {
      return record.id;
    }

    // 获取所有字段的键值对
    const fieldEntries = Object.entries(record.fields);
    
    // 过滤掉空值，找到第一个有意义的字段
    const meaningfulField = fieldEntries.find(([key, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    });

    if (meaningfulField) {
      const [fieldName, fieldValue] = meaningfulField;
      // 如果有多个有效字段，显示前几个
      const validFields = fieldEntries
        .filter(([k, v]) => v && (typeof v !== 'string' || v.trim() !== ''))
        .slice(0, 3)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      
      return validFields || String(fieldValue);
    }

    return record.id;
  };

  // 最多展示6个字段，超出合并为“更多...”
  const MAX_FIELDS = 6;
  const tableFields = fields.map(f => f.name).slice(0, MAX_FIELDS);
  const hasMoreFields = fields.length > MAX_FIELDS;

  // 到店时间只展示年月日
  // 直接返回excel原始内容
  const formatDate = (val: any) => val ?? '';

  // 渲染表格行
  // 只做纯预览
  const renderTableRows = (records: any[], showMatched = false) => (
    records.map((record, idx) => (
      <tr key={String(record.id)} className={showMatched ? '' : 'unmatched'}>
        <td style={{textAlign:'center'}}>{idx + 1}</td>
        {tableFields.map(field => {
          let val = record.fields[field];
          if (val && typeof val === 'object') {
            if ('name' in val) val = val.name;
            else if ('value' in val) val = val.value;
            else val = JSON.stringify(val);
          }
          if (field === '到店时间') val = record.fields[field];
          return <td key={field} style={{wordBreak:'break-all',whiteSpace:'pre-line',textAlign:'center'}}>{val ?? ''}</td>;
        })}
        {hasMoreFields && (
          <td style={{wordBreak:'break-all',whiteSpace:'pre-line',textAlign:'center'}}>
            {fields.slice(MAX_FIELDS).map(f => {
              let val = record.fields[f.name];
              if (val && typeof val === 'object') {
                if ('name' in val) val = val.name;
                else if ('value' in val) val = val.value;
                else val = JSON.stringify(val);
              }
              if (f.name === '到店时间') val = record.fields[f.name];
              return val ? `${f.name}: ${val}` : '';
            }).filter(Boolean).join('; ')}
          </td>
        )}
        {showMatched && <td style={{ color: '#52c41a', fontWeight: 500, textAlign:'center' }}>{typeof record.matchedValue === 'object' ? JSON.stringify(record.matchedValue) : (record.matchedValue || '')}</td>}
        {!showMatched && <td style={{ color: '#888', fontWeight: 500, textAlign:'center' }}>{record.matchedValue ? `尝试匹配: ${typeof record.matchedValue === 'object' ? JSON.stringify(record.matchedValue) : record.matchedValue}` : ''}</td>}
      </tr>
    ))
  );



  return (
    <div className="message-preview">
      <div className="preview-header">
        <h3 style={{ fontWeight: 500, fontSize: '1.08rem', margin: 0 }}>匹配结果预览</h3>
        <div className="preview-stats">
          <Tag color="blue">{matchingResult.groups.length} 个群</Tag>
          <Tag color="green">{matchingResult.matchedCount} 条记录</Tag>
          {matchingResult.unmatchedCount > 0 && (
            <Tag color="orange">{matchingResult.unmatchedCount} 条未匹配</Tag>
          )}
        </div>

      </div>

      {/* 已匹配分组表格 */}
      {matchingResult.groups.map((group, index) => (
        <Card key={index} style={{ margin: '10px 0', padding: '8px' }} title={group.groupName + `（${group.records.length}条）`}>
          <div className="table-scroll-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign:'center' }}>序号</th>
                  {tableFields.map(field => <th key={field}>{field}</th>)}
                  {hasMoreFields && <th>更多</th>}
                  <th>匹配值</th>
                </tr>
              </thead>
              <tbody>
                {renderTableRows(group.records, true)}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {/* 未匹配记录表格 */}
      {matchingResult.unmatchedCount > 0 && (
        <Card style={{ margin: '10px 0', padding: '8px' }} title={`未匹配记录（${matchingResult.unmatchedCount}条）`}>
          <div className="table-scroll-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign:'center' }}>序号</th>
                  {tableFields.map(field => <th key={field}>{field}</th>)}
                  {hasMoreFields && <th>更多</th>}
                  <th>匹配情况</th>
                </tr>
              </thead>
              <tbody>
                {renderTableRows(matchingResult.unmatchedRecords, false)}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
